/**
 * Loading a stored template as a document — and the legacy design envelope.
 *
 * The design document lives in `email_templates.design_document` (plain JSON,
 * carried by the API as the template's `design_document` field). Before that
 * column existed it rode inside the `body` as a base64url Twig comment —
 * `{# cvn:design v1 <base64url> #}` — and bodies saved in that era still carry
 * it. The read side here stays indefinitely: `loadDocument` prefers the stored
 * document and falls back to decoding the envelope, so an envelope-era body
 * (including any history version restored from one) opens exactly as before.
 * The backend strips the envelope into the column on each template's next
 * save, so the write side (`encodeEnvelope`/`writeEnvelope`) survives only for
 * tests and tooling.
 *
 * Nothing here throws: a body edited by hand may carry a truncated, corrupted
 * or hand-deleted envelope, and the editor must degrade to the legacy path
 * rather than fail to open a template.
 */
import {
  DOCUMENT_VERSION,
  DocumentFidelity,
  EmailDocument,
  createEmptyDocument,
  createLayoutedLegacyDocument,
  createLegacyDocument,
  upgradeContainedDocument,
} from './document';

/**
 * Matches one envelope comment. The payload is base64url (no padding), so it
 * can never contain `#}` and terminate the comment early.
 */
const ENVELOPE_PATTERN = /\{#\s*cvn:design\s+v(\d+)\s+([A-Za-z0-9_-]+)\s*#\}/g;

/** Encodes UTF-8 text as unpadded base64url (`btoa` alone breaks on non-ASCII). */
function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Inverse of `toBase64Url`; returns null when the payload is not decodable. */
function fromBase64Url(payload: string): string | null {
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export function encodeEnvelope(document: EmailDocument): string {
  return `{# cvn:design v${DOCUMENT_VERSION} ${toBase64Url(
    JSON.stringify(document),
  )} #}`;
}

/** Every envelope comment removed, with the trailing blank line it added. */
export function stripEnvelope(body: string): string {
  return body.replace(ENVELOPE_PATTERN, '').replace(/\n+$/, '');
}

/**
 * The document carried by the body's envelope, or null when there is none or
 * it cannot be decoded. The *last* envelope wins: appending is how `compile`
 * writes, so a body that somehow accumulated two carries the newer one last.
 */
export function readEnvelope(body: string): EmailDocument | null {
  const matches = [...body.matchAll(ENVELOPE_PATTERN)];
  const match = matches[matches.length - 1];
  if (!match) return null;

  // The marker version is readable without decoding, so it gates first: a body
  // written by a newer editor build is treated as "no envelope" and opens as
  // legacy rather than as a half-understood document.
  if (Number(match[1]) > DOCUMENT_VERSION) return null;

  const json = fromBase64Url(match[2]);
  if (json === null) return null;

  try {
    return coerceDocument(JSON.parse(json));
  } catch {
    return null;
  }
}

/**
 * A parsed value as a document, or null when its shape cannot be trusted.
 *
 * Shared by the envelope decode and the stored `design_document` field: a
 * future editor version may have written a shape this build cannot read, and
 * treating that as "no document" keeps the body openable (as legacy) instead
 * of rendering a half-understood document.
 */
export function coerceDocument(parsed: unknown): EmailDocument | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const candidate = parsed as EmailDocument;
  if (!Array.isArray(candidate.blocks)) return null;
  if (Number(candidate.v) > DOCUMENT_VERSION) return null;
  return candidate;
}

/** Replaces any existing envelope with one carrying `document`. */
export function writeEnvelope(body: string, document: EmailDocument): string {
  const clean = stripEnvelope(body);
  return `${clean}\n${encodeEnvelope(document)}\n`;
}

export interface LoadedDocument {
  document: EmailDocument;
  fidelity: DocumentFidelity;
}

/**
 * Opens a stored body as a document.
 *
 * Tier 0 (`exact`)  — the stored `design_document` field, when the template
 *                     has one.
 * Tier 1 (`exact`)  — decode the body's legacy `{# cvn:design #}` envelope.
 * Tier 2 (`high`)   — split a layouted body into its `{% block %}` overrides,
 *                     one raw-HTML block per region.
 * Tier 3 (`legacy`) — hold the whole body in one raw-HTML block, untouched.
 */
export function loadDocument(
  body: string,
  layoutKey: string | null = null,
  stored: unknown = null,
): LoadedDocument {
  const fromStored = coerceDocument(stored);

  /**
   * A new template has no body, and there is nothing to contain. Treating it as
   * legacy marked it `rawPassthrough`, which locks the structure — so a brand-new
   * template could not be built visually at all (found while creating one). The
   * round-trip is trivially closed for an empty document, hence `exact`.
   */
  if (body.trim() === '' && !fromStored) {
    return { document: createEmptyDocument({ layoutKey }), fidelity: 'exact' };
  }

  const document = fromStored ?? readEnvelope(body);
  if (document) {
    // `high`, never `exact`: the upgraded document no longer compiles to the body
    // it was read from, and calling it exact would have the divergence check read
    // that as a hand edit and detach the template on open.
    const upgraded = layoutKey
      ? upgradeContainedDocument(document, layoutKey)
      : null;
    if (upgraded) return { document: upgraded, fidelity: 'high' };

    return {
      document,
      fidelity: document.detached ? 'detached' : 'exact',
    };
  }

  /**
   * Tier 2 (`high`) — a body that names a layout is a set of `{% block %}`
   * overrides, and each override is a region the canvas can hold as its own
   * block. Tried before containment so these templates open as an editable
   * email rather than as one locked blob.
   */
  if (layoutKey) {
    const layouted = createLayoutedLegacyDocument(body, layoutKey);
    if (layouted) return { document: layouted, fidelity: 'high' };
  }

  return {
    document: createLegacyDocument(body, layoutKey),
    fidelity: 'legacy',
  };
}
